import React from 'react';
import { Text, View, ViewStyle, TextStyle } from 'react-native';
import { colorPalette } from '@shared/config/colorsConfig';
import { typography } from '@shared/config/typographyConfig';


export type BadgeVariant = 'warning' | 'success' | 'primary';

export type BadgeSize = 'small' | 'medium' | 'large';

export interface IBadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children?: React.ReactNode;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

const Badge: React.FC<IBadgeProps> = ({
  variant = 'success',
  size = 'medium',
  children,
  style,
  textStyle,
}) => {
  const getBackgroundColor = () => {
    switch (variant) {
      case 'warning':
        return colorPalette.warning[100];
      case 'primary':
        return colorPalette.primary[100];
      case 'success':
        return colorPalette.success[100];
      default:
        return colorPalette.success[100];
    }
  };

  const getTextColor = () => {
    switch (variant) {
      case 'warning':
        return colorPalette.warning[700];
      case 'primary':
        return colorPalette.primary[700];
      case 'success':
        return colorPalette.success[700];
      default:
        return colorPalette.success[700];
    }
  };

  const getBorderColor = () => {
    switch (variant) {
      case 'warning':
        return colorPalette.warning[500];
      case 'primary':
        return colorPalette.primary[500];
      case 'success':
        return colorPalette.success[500];
      default:
        return colorPalette.success[500];
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          paddingHorizontal: 6,
          paddingVertical: 2,
          borderRadius: 4,
        };
      case 'medium':
        return {
          paddingHorizontal: 8,
          paddingVertical: 3,
          borderRadius: 16,
        };
      case 'large':
        return {
          paddingHorizontal: 12,
          paddingVertical: 6,
          borderRadius: 32,
        };
      default:
        return {
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 6,
        };
    }
  };

  const getTextSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          fontSize: typography.fontSize.xs,
          fontWeight: '500' as const,
        };
      case 'medium':
        return {
          fontSize: typography.fontSize.sm,
          fontWeight: '500' as const,
        };
      case 'large':
        return {
          fontSize: typography.fontSize.base,
          fontWeight: '600' as const,
        };
      default:
        return {
          fontSize: typography.fontSize.sm,
          fontWeight: '500' as const,
        };
    }
  };

  const containerStyle: ViewStyle = {
    backgroundColor: getBackgroundColor(),
    borderWidth: 1,
    borderColor: getBorderColor(),
    alignSelf: 'flex-start',
    ...getSizeStyles(),
    ...style,
  };

  const defaultTextStyle: TextStyle = {
    color: getTextColor(),
    textAlign: 'center',
    ...getTextSizeStyles(),
    ...textStyle,
  };

  return (
    <View style={containerStyle}>
      <Text style={defaultTextStyle}>{children}</Text>
    </View>
  );
};

export default Badge;
